/**
 * O ATUALIZADOR DAS TELAS DO APLICATIVO (1.4, 19/10/2026) — a decisão, sem o plugin. Sem dependências do navegador além do
 * WebCrypto: o teste roda em Node (`backend/testes-cruzados/telasDoApp.test.ts`).
 *
 * O servidor (`backend/src/services/telasDoApp.ts`) oferece o manifesto do canal; aqui se decide se o pacote serve:
 *   1. a ASSINATURA do manifesto confere com a chave pública que vai no aplicativo (`chaveDasTelas.json`);
 *   2. o build nativo deste aparelho é pelo menos o mínimo do pacote (o servidor já filtra; conferimos de novo);
 *   3. a versão é mais nova que a que está rodando.
 * O SHA-256 do zip quem confere é o plugin, no download (`checksum`).
 *
 * QUANDO: nunca na página da prova (`foraDaProva`), e o pacote baixado só entra na PRÓXIMA ABERTURA do aplicativo — o
 * plugin aplica `next` também ao ir para segundo plano, e por isso o componente acrescenta a condição `kill`.
 */
export type Plataforma = 'android' | 'ios';
export interface ManifestoDasTelas {
  canal: 'teste' | 'estavel';
  versao: string;
  arquivo: string;
  sha256: string;
  tamanho: number;
  nativoMinimo: Record<Plataforma, number>;
  publicadoEm: string;
  assinatura: string;
}
export type Oferta = { disponivel: true; manifesto: ManifestoDasTelas } | { disponivel: false; motivo: string };

/** O MESMO texto de `backend/src/services/telasDoApp.ts` (`textoAssinado`) — mexeu num, mexa no outro. */
export function textoAssinado(m: Omit<ManifestoDasTelas, 'assinatura'>): string {
  return ['portal-simulados-telas-v1', m.canal, m.versao, m.arquivo, m.sha256, String(m.tamanho), String(m.nativoMinimo.android), String(m.nativoMinimo.ios), m.publicadoEm].join('|');
}

const deBase64 = (b64: string): Uint8Array => {
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
};

export async function assinaturaConfere(m: ManifestoDasTelas, jwk: JsonWebKey, sutil: SubtleCrypto = globalThis.crypto.subtle): Promise<boolean> {
  try {
    const { assinatura, ...resto } = m;
    const chave = await sutil.importKey('jwk', { ...jwk, ext: true }, { name: 'ECDSA', namedCurve: 'P-256' }, false, ['verify']);
    return await sutil.verify({ name: "ECDSA", hash: "SHA-256" }, chave, deBase64(assinatura) as BufferSource, new TextEncoder().encode(textoAssinado(resto)) as BufferSource);
  } catch {
    return false;
  }
}

export type Decisao = { baixar: true; manifesto: ManifestoDasTelas } | { baixar: false; motivo: string };

/** Serve? A ordem dos "não" é a do relato: o que a pessoa que lê o registro precisa saber primeiro. */
export async function decidir(oferta: Oferta, aparelho: { plataforma: Plataforma; build: number; versaoAtual: string | null }, jwk: JsonWebKey, sutil?: SubtleCrypto): Promise<Decisao> {
  if (!oferta.disponivel) return { baixar: false, motivo: oferta.motivo };
  const m = oferta.manifesto;
  if (!(await assinaturaConfere(m, jwk, sutil))) return { baixar: false, motivo: 'assinatura não confere — pacote recusado' };
  const minimo = Number(m.nativoMinimo?.[aparelho.plataforma]);
  if (!Number.isInteger(minimo) || aparelho.build < minimo) return { baixar: false, motivo: `pacote para o build nativo ${minimo}+, este aparelho tem ${aparelho.build}` };
  if (aparelho.versaoAtual && m.versao <= aparelho.versaoAtual) return { baixar: false, motivo: 'já está na versão mais nova' };
  return { baixar: true, manifesto: m };
}

// O canal deste aparelho mora em `canalDoAparelho.ts` (armazenamento nativo; 20/10/2026).
